import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { InputText } from "primereact/inputtext";
import DeleteIconButton from "./deleteIconButton";
import { ReportRow, ReportType } from "../api/models/report.models";
import { useDebounce } from "primereact/hooks";
import { UpdateReportRowPayload } from "../api/models/reportRow.models";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  compareReportRowWithFreshValues,
  convertAkeneoRowsToEcomodRows,
  getReportTotalWeight,
  getRowTotalWeight,
  isEcomodReport,
  isEcomodRow,
  ReportRowComparison,
} from "../utils/rowUtils";
import { ReportRowType } from "../hooks/useReportRows";
import useRefreshReportRow from "../hooks/useRefreshReportRow";
import { useReportFormContext } from "../contexts/reportFormContext";
import { Tooltip } from "primereact/tooltip";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { getFormalLocale } from "../utils/appUtils";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import RefreshReportRowDialog from "../dialogs/refreshReportRowDialog";

// full width table
const TableContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  overflow: hidden;
  padding: 0 2rem;
  gap: 2rem;
  & h3 {
    align-self: flex-start;
    font-weight: 500;
    font-size: 1.2rem;
    margin: 2rem 0 0 0;
  }
`;

const StyledTable = styled(DataTable)`
  min-width: 75vw;

  @media (max-width: 1268px) {
    min-width: 65vw;
  }

  @media (max-width: 968px) {
    min-width: 50vw;
  }

  & .edit-product-link {
    color: var(--sumi-light-blue);
  }

  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 1.5rem 1rem;
  }

  .p-datatable-thead > tr > th {
    border-bottom: 1px solid #333333;
    font-weight: 500;
    font-size: 0.8rem;
    color: #888888;
    padding: 1rem;
  }

  // rotate svg icon
  & .p-row-toggler svg {
    color: var(--text-color);
    transform: rotate(90deg);
  }

  // rotate 180 degrees when attribute expanded is true
  & .p-row-toggler[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  .p-datatable-row-expansion {
    > td {
      padding: 0;
      border: none;
    }

    & .p-datatable-tbody > tr > td {
      background-color: var(--sumi-background-blue);
    }

    & .p-datatable-tbody > tr > td {
      border: none;
    }
  }
`;

const TotalWeight = styled.span`
  font-size: 1rem;
  color: var(--sumi-blue);
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    text-decoration: none;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const RefreshIconButton = styled.div<{ $spinning?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;

  i {
    color: var(--sumi-blue);
    font-size: 0.9rem;
    ${({ $spinning }) =>
      $spinning ? "animation: p-icon-spin 1s linear infinite;" : ""}
  }

  &:hover i {
    opacity: 0.8;
  }
`;

interface ReportListItemProps {
  reportRows: ReportRow[];
  onRemove?: (rowId: number) => void;
  updateReportRow: (payload: UpdateReportRowPayload) => void;
  reportId: number;
  reportYear: number;
  isLoading?: boolean;
  ecomodClasses?: EcomodClass[];
  supClasses?: SupClass[];
}

interface PendingRowRefresh {
  row: ReportRow;
  comparison: ReportRowComparison;
  freshName: string;
  freshActive: boolean;
  freshValues: UpdateReportRowPayload["values"];
}

const ReportListItem: React.FC<ReportListItemProps> = ({
  reportRows,
  onRemove,
  updateReportRow,
  reportId,
  reportYear,
  isLoading,
  ecomodClasses,
  supClasses,
}) => {
  const { t } = useTranslation();
  const [locale, toast, companyId] = useUIStore(
    useShallow((state: UIState) => [
      state.locale,
      state.toastRef,
      state.companyId,
    ]),
  );
  const formalLocale = getFormalLocale(locale);

  const { refreshRow, isRefreshingRow } = useRefreshReportRow({
    companyId: companyId as number,
  });

  const [refreshingRowId, setRefreshingRowId] = useState<number | null>(null);
  const [pendingRowRefresh, setPendingRowRefresh] =
    useState<PendingRowRefresh | null>(null);

  const [editableReports, debouncedReports, setEditableReports] = useDebounce<
    ReportRow[]
  >(reportRows, 800);

  const [
    nonEditableReports,
    debouncedNotEditableReports,
    setNotEditableReports,
  ] = useDebounce<ReportRow[]>(reportRows, 800);

  const [ecomodMaxWeights] = useUIStore(
    useShallow((state: UIState) => [state.ecomodMaxWeights]),
  );

  const {
    isUpdatingReportRow,
    setCombinedWeight,
    setReportType,
    setDisplayEcomodError,
    displayEcomodError,
    setShowEditReportRowView,
  } = useReportFormContext();

  useEffect(() => {
    if (reportRows) {
      const editable = reportRows
        .filter((row) => (row.ean || row.internalReference) && row.name)
        .sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        );
      setEditableReports(editable);
      const nonEditable = reportRows
        .filter((row) => (!row.ean && !row.internalReference) || !row.name)
        .sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        );
      setNotEditableReports(nonEditable);
    }
  }, [reportRows]);

  useEffect(() => {
    if (debouncedReports) {
      debouncedReports.forEach((reportRow) => {
        const originalRow = reportRows.find((row) => row.id === reportRow.id);
        if (originalRow && reportRow.amount !== originalRow.amount) {
          const updatePayload: UpdateReportRowPayload = {
            ...originalRow,
            id: reportRow.id,
            reportId,
            name: reportRow.name,
            ean: reportRow.ean,
            amount: reportRow.amount,
            active: reportRow.active,
          };
          updateReportRow(updatePayload);
        }
      });

      // Check if the report is Ecomod or Simple
      const reportType = isEcomodReport(debouncedReports)
        ? ReportRowType.ECOMOD
        : ReportRowType.SIMPLE;

      // Calculate the total weight of all rows
      const totalWeight = getReportTotalWeight(
        debouncedReports,
        false,
        ReportType.EAN,
      );

      setReportType(reportType);
      setCombinedWeight(totalWeight);
      const maxWeight =
        ecomodMaxWeights.find((weight) => weight.year === reportYear)?.value ||
        50000;

      // Check if the total weight exceeds the limit for simple reports
      if (reportType === ReportRowType.SIMPLE && totalWeight > maxWeight) {
        setDisplayEcomodError(true);
      } else {
        setDisplayEcomodError(false);
      }
    }
  }, [debouncedReports]);

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    rowId: number,
  ) => {
    const updatedReports = editableReports.map((row) =>
      row.id === rowId ? { ...row, amount: parseInt(e.target.value) } : row,
    );
    setEditableReports(updatedReports);
  };

  // Removed for now, can be used later to fetch fresh data for a report row
  const handleRefreshRow = async (row: ReportRow) => {
    setRefreshingRowId(row.id);
    try {
      const freshData = await refreshRow(row.ean);

      if (!freshData) {
        toast?.current?.show({
          severity: "warn",
          detail: t("text-refresh-row-not-found"),
          life: 3000,
        });
        return;
      }

      const freshValues = convertAkeneoRowsToEcomodRows(
        freshData.rowValues,
        ecomodClasses || [],
        supClasses || [],
      );

      const comparison = compareReportRowWithFreshValues(
        row,
        freshData.name,
        freshData.active,
        freshValues,
        locale,
      );

      if (!comparison.hasChanges) {
        toast?.current?.show({
          severity: "info",
          detail: t("text-no-changes-detected"),
          life: 3000,
        });
        return;
      }

      setPendingRowRefresh({
        row,
        comparison,
        freshName: freshData.name,
        freshActive: freshData.active,
        freshValues,
      });
    } catch (error) {
      console.error("Failed to refresh report row:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-failed-to-refresh-row"),
        life: 3000,
      });
    } finally {
      setRefreshingRowId(null);
    }
  };

  const handleConfirmRowRefresh = () => {
    if (!pendingRowRefresh) {
      return;
    }

    const { row, freshName, freshActive, freshValues } = pendingRowRefresh;

    const updatePayload: UpdateReportRowPayload = {
      ...row,
      id: row.id,
      reportId,
      name: freshName,
      ean: row.ean,
      amount: row.amount,
      active: freshActive,
      values: freshValues,
    };

    updateReportRow(updatePayload);
    setPendingRowRefresh(null);
  };

  const actionTemplate = (rowData: ReportRow) => (
    <ActionButtons>
      {/* <RefreshIconButton
        role="button"
        tabIndex={0}
        title={t("cmd-refresh-row")}
        $spinning={refreshingRowId === rowData.id && isRefreshingRow}
        onClick={() => handleRefreshRow(rowData)}
      >
        <i className="pi pi-refresh" />
      </RefreshIconButton> */}
      <DeleteIconButton onRemove={() => onRemove && onRemove(rowData.id)} />
    </ActionButtons>
  );

  const amountTemplate = (rowData: ReportRow) => (
    <InputText
      value={rowData.amount.toString()}
      onChange={(e) => onInputChange(e, rowData.id)}
    />
  );

  // fix javascript rounding issue
  const toMeaningfulNumber = (value: number) => {
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toLocaleString(formalLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const totalTemplate = (rowData: ReportRow) => {
    const totalWeight = getRowTotalWeight(rowData);
    const total = rowData.amount * totalWeight;
    return (
      <TotalWeight
        role="button"
        tabIndex={0}
        onClick={() => setShowEditReportRowView(rowData)}
      >
        {toMeaningfulNumber(total)} kg
      </TotalWeight>
    );
  };

  // shows "(n/total)" suffix when several rows share the same non-empty EAN so users can tell them apart
  const eanTemplate = (rowData: ReportRow) => {
    if (!rowData.ean) {
      return rowData.ean;
    }
    const sameEanRows = editableReports.filter(
      (row) => row.ean === rowData.ean,
    );
    if (sameEanRows.length <= 1) {
      return rowData.ean;
    }
    const position = sameEanRows.findIndex((row) => row.id === rowData.id) + 1;
    return `${rowData.ean} (${position}/${sameEanRows.length})`;
  };

  // name template, show name and error icon is row type is simple and ecomod warning is true
  const nameTemplate = (rowData: ReportRow) => {
    const isSimple = !isEcomodRow(rowData);
    const showErrorIcon = isSimple && displayEcomodError;

    return (
      <div>
        {rowData.name}
        {showErrorIcon && (
          <>
            <Tooltip target={".ecomod-warning-icon"} />
            <span
              className="ecomod-warning-icon pi pi-exclamation-triangle"
              style={{ color: "red", marginLeft: "0.5rem" }}
              data-pr-tooltip={t("label-ecomod-warning")}
              data-pr-position="top"
            />
          </>
        )}
      </div>
    );
  };

  return (
    <TableContainer>
      <StyledTable
        value={editableReports}
        dataKey="id"
        scrollable
        emptyMessage={t("text-no-products-reported")}
      >
        <Column field="ean" header={t("label-ean")} body={eanTemplate} />
        <Column field="internalReference" header={t("label-product-number")} />
        <Column
          field="amount"
          header={t("label-amount-pcs")}
          body={amountTemplate}
        />
        <Column
          field="name"
          header={t("label-product-name")}
          body={nameTemplate}
        />
        <Column header={t("label-amount-kg")} body={totalTemplate} />
        <Column body={actionTemplate} />
      </StyledTable>

      {nonEditableReports.length > 0 && (
        <>
          <h3>{t("header-material-rows")}</h3>
          <StyledTable value={nonEditableReports} dataKey="id" scrollable>
            <Column
              field="name"
              header={t("label-product-name")}
              body={nameTemplate}
            />
            <Column header={t("label-amount-kg")} body={totalTemplate} />
            <Column body={actionTemplate} />
          </StyledTable>
        </>
      )}

      <RefreshReportRowDialog
        visible={!!pendingRowRefresh}
        comparison={pendingRowRefresh?.comparison || null}
        onConfirm={handleConfirmRowRefresh}
        onHide={() => setPendingRowRefresh(null)}
      />
    </TableContainer>
  );
};

export default ReportListItem;
